/**
 * Blog post content store.
 * Each post is a list of typed content blocks rendered by blog-post.tsx.
 * Inline formatting in "p" and "list" blocks:
 *   **bold** → <strong>
 *   `code`   → <code>
 *   [text](url) → <a>
 */

export type Block =
  | { t: "h2"; text: string }
  | { t: "h3"; text: string }
  | { t: "p"; text: string }
  | { t: "code"; lang: string; src: string }
  | { t: "callout"; kind: "tip" | "warn" | "insight" | "note"; text: string }
  | { t: "diagram"; id: "api-to-mcp-flow" | "mcp-vs-direct" | "tool-call-flow" }
  | { t: "list"; ordered?: boolean; items: string[] }
  | { t: "quote"; text: string; attribution?: string }
  | { t: "divider" };

export type BlogPost = {
  slug: string;
  title: string;
  description: string;
  publishedAt: string;
  readingMins: number;
  tags: string[];
  coverGradient: string;
  /** Optional PNG/JPG path for og:image — SVG not supported by Twitter/Slack */
  ogImage?: string;
  blocks: Block[];
};

// ─── Post 1 ───────────────────────────────────────────────────────────────────

const post1: BlogPost = {
  slug: "how-to-connect-claude-to-any-rest-api",
  title: "How to Connect Claude to Any REST API",
  description:
    "Most developers use Claude to write code or explain things. Here's how to give it live access to your actual API — so it can look up real data, answer real questions, and stop needing you as the middleman.",
  publishedAt: "2026-05-27",
  readingMins: 7,
  tags: ["MCP", "Claude", "Tutorial", "OpenAPI"],
  coverGradient: "from-blue-100 via-indigo-100 to-violet-100",
  blocks: [
    {
      t: "p",
      text: "Last month I wanted Claude to help me debug a customer support issue. The customer had a subscription, something went wrong mid-cycle, and I needed to understand the charge history. My Stripe account was right there. Claude was right there. And yet I spent ten minutes copying JSON from the Stripe dashboard, pasting it into the chat, asking Claude to make sense of it.",
    },
    {
      t: "p",
      text: "That's the gap MCP fills. Not AI magic — just a clean way to give Claude callable access to an API so it can fetch the data itself instead of waiting for you to paste it in.",
    },
    {
      t: "h2",
      text: "What actually happens under the hood",
    },
    {
      t: "p",
      text: "Claude can't make HTTP requests. What it can do is call **tools** — named functions with defined inputs and outputs. MCP (Model Context Protocol) is a standard that defines how those tools are described and called. An MCP server is just an HTTP service that exposes your API's endpoints as MCP tools.",
    },
    {
      t: "p",
      text: "When you add an MCP server to Claude Desktop, Claude learns what tools are available and their schemas. Ask it something relevant and it decides which tool to call, constructs the arguments, gets the response, and incorporates it into its answer. You see the result. You don't see the HTTP request.",
    },
    {
      t: "diagram",
      id: "tool-call-flow",
    },
    {
      t: "h2",
      text: "The two-minute setup",
    },
    {
      t: "p",
      text: "If your API has an OpenAPI spec (most do — check for `/openapi.json`, `/swagger.json`, or a link in the docs), you're 90% of the way there. The spec already describes every endpoint, its parameters, and its response shape. Converting it to a live MCP server takes one step.",
    },
    {
      t: "list",
      ordered: true,
      items: [
        "Find your API's OpenAPI spec URL (or download the file)",
        "Paste it into [API to MCP Converter](https://apitomcpconverter.com)",
        "Get back a live MCP endpoint URL",
        "Add the URL to your Claude Desktop config",
      ],
    },
    {
      t: "diagram",
      id: "api-to-mcp-flow",
    },
    {
      t: "h2",
      text: "Adding the MCP server to Claude Desktop",
    },
    {
      t: "p",
      text: "Open `~/Library/Application Support/Claude/claude_desktop_config.json` (create it if it doesn't exist). Add your MCP server under `mcpServers`:",
    },
    {
      t: "code",
      lang: "json",
      src: `{
  "mcpServers": {
    "stripe": {
      "url": "https://api-to-mcp.onrender.com/mcp/stripe-mcp",
      "headers": {
        "Authorization": "Bearer sk_test_YOUR_KEY_HERE"
      }
    }
  }
}`,
    },
    {
      t: "p",
      text: "Restart Claude Desktop. Open a new conversation. Ask: **\"What tools do you have available?\"** — Claude will list everything your MCP exposes. That's your signal it's working.",
    },
    {
      t: "callout",
      kind: "tip",
      text: "If you have multiple MCP servers, they all go under `mcpServers` as separate keys. Claude can use all of them in the same conversation.",
    },
    {
      t: "h2",
      text: "Cursor and Cline work the same way",
    },
    {
      t: "p",
      text: "Cursor uses `.cursor/mcp.json` in your project root (or globally at `~/.cursor/mcp.json`). The format is identical:",
    },
    {
      t: "code",
      lang: "json",
      src: `{
  "mcpServers": {
    "stripe": {
      "url": "https://api-to-mcp.onrender.com/mcp/stripe-mcp",
      "headers": {
        "Authorization": "Bearer sk_test_YOUR_KEY_HERE"
      }
    }
  }
}`,
    },
    {
      t: "p",
      text: "Cline (the VS Code extension) has a dedicated MCP settings panel. Paste the URL and header there. Same result.",
    },
    {
      t: "h2",
      text: "Authentication — the part that trips people up",
    },
    {
      t: "p",
      text: "Most production APIs require auth. The converter handles this by forwarding whatever headers you specify in the config to every request it makes on your behalf. For a Bearer token API, that's the `Authorization` header. For an API that uses a custom header like `X-API-Key`, use that instead:",
    },
    {
      t: "code",
      lang: "json",
      src: `{
  "mcpServers": {
    "my-internal-api": {
      "url": "https://api-to-mcp.onrender.com/mcp/my-api-slug",
      "headers": {
        "X-API-Key": "YOUR_KEY_HERE"
      }
    }
  }
}`,
    },
    {
      t: "callout",
      kind: "warn",
      text: "Your API key lives in a local config file on your machine — it's not sent to Anthropic or stored by Claude. But treat it like any other credential: don't commit this file to git.",
    },
    {
      t: "h2",
      text: "What to actually ask Claude once it's connected",
    },
    {
      t: "p",
      text: "The biggest mistake people make after setting up an MCP is asking vague questions. Claude is good at using tools, but it works better with specific intent. Instead of \"tell me about my Stripe account\", try:",
    },
    {
      t: "list",
      items: [
        "\"List the last 5 charges for customer ID cus_abc123 and tell me if any failed\"",
        "\"What's the current status of subscription sub_xyz and when does it renew?\"",
        "\"Find all customers who signed up this week and haven't made a payment yet\"",
      ],
    },
    {
      t: "p",
      text: "The more context you give, the better Claude can decide which tools to chain together to answer the question.",
    },
    {
      t: "h2",
      text: "When the spec doesn't exist",
    },
    {
      t: "p",
      text: "Not every API has a published OpenAPI spec. Internal APIs, older services, and some third-party APIs just have HTML documentation. In that case, you have a few options:",
    },
    {
      t: "list",
      items: [
        "**Write a minimal spec yourself.** You don't need to document every endpoint — just the ones you want Claude to use. A spec with 5 endpoints is fine.",
        "**Use a tool like Postman.** Import the API docs as a Postman collection, then export as OpenAPI. Postman handles the conversion reasonably well.",
        "**Paste the docs and ask Claude to generate a spec.** Seriously — paste the API documentation into a Claude conversation and ask it to write an OpenAPI 3.0 YAML. It does this well.",
      ],
    },
    {
      t: "h2",
      text: "The honest limitation",
    },
    {
      t: "p",
      text: "This works best for read-heavy workflows — looking up data, checking status, querying records. Write operations (creating, deleting, charging) work too, but be careful. Claude will do exactly what you ask. If you ask it to cancel all subscriptions in a date range and you got the date wrong, it'll cancel them.",
    },
    {
      t: "quote",
      text: "Give Claude read access by default. Add write access deliberately, for specific operations you've thought through.",
    },
    {
      t: "p",
      text: "For Stripe specifically, Stripe's restricted keys let you scope exactly which operations a key can perform. That's worth using.",
    },
    {
      t: "divider",
    },
    {
      t: "p",
      text: "The whole point of this setup is to stop being the data retrieval layer between Claude and your tools. Once it's working, you'll stop opening dashboards for quick lookups. That's the payoff — Claude becomes genuinely useful for your actual workflow, not just your coding workflow.",
    },
  ],
};

// ─── Post 2 ───────────────────────────────────────────────────────────────────

const post2: BlogPost = {
  slug: "mcp-vs-direct-api-calls",
  title: "MCP vs Direct API Calls: What Actually Changes",
  description:
    "Most explanations of MCP focus on what it is. This one focuses on the part that matters: what changes in your actual workflow, and when you should and shouldn't use it.",
  publishedAt: "2026-05-20",
  readingMins: 6,
  tags: ["MCP", "Architecture", "Claude", "API"],
  coverGradient: "from-emerald-100 via-teal-100 to-cyan-100",
  blocks: [
    {
      t: "p",
      text: "\"Do I actually need MCP?\" is the right question to ask. Most blog posts about MCP skip it and jump straight into protocol details. Let me try to answer it properly.",
    },
    {
      t: "p",
      text: "The short answer: if you're calling an API from code, you don't need MCP. If you want Claude to call an API, you do. The difference is the caller.",
    },
    {
      t: "h2",
      text: "The problem MCP actually solves",
    },
    {
      t: "p",
      text: "Claude is a language model. It processes text in, generates text out. It can't open sockets, make HTTP requests, or read from a database. When you want it to interact with live data, someone has to bridge that gap.",
    },
    {
      t: "p",
      text: "Before MCP, that bridge was you. You'd fetch the data yourself, paste it into the prompt, and ask Claude to reason over it. It worked, but it was tedious — and it meant Claude was always working with a snapshot of the data you happened to copy, not the live state.",
    },
    {
      t: "p",
      text: "MCP moves the bridge. Instead of you fetching and pasting, Claude calls a tool directly. The tool makes the actual HTTP request. Claude gets the live response.",
    },
    {
      t: "diagram",
      id: "mcp-vs-direct",
    },
    {
      t: "h2",
      text: "What the protocol actually looks like",
    },
    {
      t: "p",
      text: "MCP is a JSON-RPC protocol over HTTP with Server-Sent Events. When Claude connects to an MCP server, the first thing it does is call `tools/list` to discover what's available:",
    },
    {
      t: "code",
      lang: "json",
      src: `// Claude sends:
{
  "method": "tools/list"
}

// MCP server responds with available tools:
{
  "tools": [
    {
      "name": "list_customers",
      "description": "List customers with optional filters",
      "inputSchema": {
        "type": "object",
        "properties": {
          "limit": { "type": "integer", "default": 10 },
          "email": { "type": "string" }
        }
      }
    }
  ]
}`,
    },
    {
      t: "p",
      text: "When you ask Claude something relevant, it calls `tools/call` with the tool name and arguments it constructs from your request. The MCP server makes the actual HTTP call to your API and returns the result. Claude incorporates that result into its response.",
    },
    {
      t: "callout",
      kind: "insight",
      text: "Claude decides when to call a tool and what arguments to pass. You write the prompt in plain English. The translation from intent to API parameters is Claude's job, not yours.",
    },
    {
      t: "h2",
      text: "The latency story",
    },
    {
      t: "p",
      text: "MCP adds a round trip. Your message goes to Claude, Claude decides to call a tool, the tool call goes to the MCP server, the MCP server calls your API, the response comes back, Claude processes it, then responds to you. That's more steps than typing a query directly.",
    },
    {
      t: "p",
      text: "In practice, for conversational use this doesn't matter. You're not looking for sub-100ms responses in a chat interface. For a complex query where Claude chains 3-4 tool calls, you might wait 5-10 seconds. That's acceptable when the alternative is doing those lookups manually.",
    },
    {
      t: "callout",
      kind: "warn",
      text: "For background jobs, scheduled tasks, or anything batch-processing large amounts of data — use direct API calls from code. MCP is for interactive, conversational access.",
    },
    {
      t: "h2",
      text: "Authentication flows differently",
    },
    {
      t: "p",
      text: "With direct API calls, your token lives in your code (environment variables, secrets manager, wherever you put it). With MCP, the token lives in your Claude Desktop config and gets forwarded by the MCP server on every tool call.",
    },
    {
      t: "p",
      text: "This is both simpler and slightly different. Simpler because you configure it once. Different because the token now passes through the MCP server layer. If you're using a self-hosted MCP server, that's fine. If you're using a hosted one, verify their data handling policy.",
    },
    {
      t: "h2",
      text: "Multi-tool queries are where it gets interesting",
    },
    {
      t: "p",
      text: "The real advantage of MCP isn't single lookups — it's Claude's ability to chain multiple tool calls to answer a compound question. Ask it \"who are my highest-value customers who haven't renewed this month?\" and Claude will:",
    },
    {
      t: "list",
      ordered: true,
      items: [
        "Call `list_subscriptions` with status=past_due",
        "Call `get_customer` for each result to get their lifetime value",
        "Sort and summarise the combined data",
        "Give you a ranked list with context",
      ],
    },
    {
      t: "p",
      text: "Writing that as code would take 20-30 minutes. With MCP it's one sentence. That's the compounding value.",
    },
    {
      t: "h2",
      text: "When direct API calls are still the right choice",
    },
    {
      t: "p",
      text: "MCP isn't trying to replace your application code. Use direct API calls when:",
    },
    {
      t: "list",
      items: [
        "You need consistent, automated execution (crons, webhooks, pipelines)",
        "You're processing large datasets where latency compounds",
        "You need deterministic behavior — same input, same output, every time",
        "You're writing production application logic that other systems depend on",
      ],
    },
    {
      t: "p",
      text: "Use MCP when a human is in the loop asking questions, investigating issues, or exploring data. The heuristic: if you'd normally open a dashboard or write a one-off script to answer a question, MCP is probably faster.",
    },
    {
      t: "h2",
      text: "The thing nobody mentions",
    },
    {
      t: "p",
      text: "Once you have an API connected via MCP, you stop needing to know the API's exact parameter names and response shapes. You interact in plain English and Claude handles the translation. For APIs you use occasionally (not daily), this is a genuine productivity gain — you stop re-reading docs every time.",
    },
    {
      t: "quote",
      text: "MCP turns occasional-use APIs into first-class tools. The ones you couldn't justify learning deeply because you only need them once a month.",
    },
    {
      t: "divider",
    },
    {
      t: "p",
      text: "MCP and direct API calls aren't competing approaches. They're for different callers. Pick the right tool for who's doing the calling.",
    },
  ],
};

// ─── Post 3 ───────────────────────────────────────────────────────────────────

const post3: BlogPost = {
  slug: "best-mcp-servers-2026",
  title: "The MCP Servers Actually Worth Using in 2026",
  description:
    "I've tested a lot of MCP servers. Most are impressive demos that fall apart in daily use. Here's what actually stuck — and why.",
  publishedAt: "2026-05-13",
  readingMins: 8,
  tags: ["MCP", "Tools", "Claude", "Productivity"],
  coverGradient: "from-violet-100 via-purple-100 to-pink-100",
  blocks: [
    {
      t: "p",
      text: "The MCP server list on GitHub has over 300 entries. I've tried probably 40 of them. Most fall into one of three failure modes: they're impressive demos that don't handle real data well, they require complex setup that breaks when your environment changes, or they cover an API you'd never actually use through Claude.",
    },
    {
      t: "p",
      text: "Here's the list of what actually held up in daily use — with the specific workflow that made each one worth keeping.",
    },
    {
      t: "h2",
      text: "Linear — the best daily driver",
    },
    {
      t: "p",
      text: "The killer use case for Linear MCP isn't creating issues (you can do that in the UI faster). It's the daily standup question: **\"What should I actually work on today?\"**",
    },
    {
      t: "p",
      text: "Ask Claude this with Linear connected and it reads your open issues, checks their priorities and due dates, looks at what's blocked, and gives you a ranked working list with reasoning. That takes it about 6 seconds and would take you 3 minutes of context-switching through the board.",
    },
    {
      t: "callout",
      kind: "tip",
      text: "Try: \"Look at my Linear issues and tell me what's blocking other people that I should unblock first.\" Claude will search for issues assigned to your team members that reference yours.",
    },
    {
      t: "p",
      text: "**The limitation:** Linear's API is GraphQL under the hood, and the MCP wraps the REST-compatible endpoints. Complex queries (like \"issues that are in cycle 12 and have more than 3 comments\") sometimes require multiple tool calls where a direct GraphQL query would be cleaner.",
    },
    {
      t: "h2",
      text: "Stripe — unexpectedly good for support work",
    },
    {
      t: "p",
      text: "I expected Stripe MCP to be useful for finance reporting. It turned out to be more useful during customer support calls.",
    },
    {
      t: "p",
      text: "A customer messages saying their payment failed and they can't access the product. Old workflow: open Stripe, search for their email, click through to their subscription, check the charge history, figure out what went wrong. New workflow: paste their email into a Claude conversation with Stripe MCP connected. Claude looks them up, checks their subscription status, finds the failed charge, identifies the decline reason, and suggests what to tell them. Under 10 seconds.",
    },
    {
      t: "callout",
      kind: "warn",
      text: "Use a Stripe restricted key with read-only permissions. There's no good reason to give Claude write access to your live Stripe account for this workflow.",
    },
    {
      t: "p",
      text: "**The limitation:** Real-time webhook events and payment intent lifecycle aren't well-represented through the list/retrieve endpoints. For debugging complex payment flows (3D Secure, mandate handling), you still need the Stripe dashboard.",
    },
    {
      t: "h2",
      text: "GitHub — code search is the underrated feature",
    },
    {
      t: "p",
      text: "Everyone reaches for GitHub MCP to manage issues and PRs. That's fine but it's not where it earns its place.",
    },
    {
      t: "p",
      text: "The underrated use case is code search with reasoning on top. \"Find every place we call `sendEmail` and tell me which ones don't handle the error case\" — Claude searches your repositories, reads the relevant code, and gives you a real answer with file names and line numbers. GitHub's built-in code search shows you matches. Claude tells you what they mean.",
    },
    {
      t: "p",
      text: "For teams, the PR review workflow is also genuinely useful: \"Summarise what changed in PR #847 and flag anything that looks risky.\" Claude reads the diff and gives you an actual technical summary, not just a list of files changed.",
    },
    {
      t: "callout",
      kind: "insight",
      text: "Generate a fine-grained personal access token (not a classic token) and scope it to only the repositories you actually want Claude to access.",
    },
    {
      t: "p",
      text: "**The limitation:** GitHub's API rate limits are aggressive (5,000 requests/hour for authenticated users). If Claude chains many tool calls on a large repository, you'll hit the limit. The error messages aren't great.",
    },
    {
      t: "h2",
      text: "Notion — for knowledge retrieval, not creation",
    },
    {
      t: "p",
      text: "Notion MCP is split-personality. For querying and reading your workspace, it's excellent. For creating richly formatted content, it's mediocre.",
    },
    {
      t: "p",
      text: "The workflow it handles well: your team keeps meeting notes, decisions, and RFCs in Notion. You need to find something specific — \"what did we decide about auth in Q1?\" — but you don't know which page it's in. Claude searches the workspace, finds relevant pages, and surfaces the actual decision. That used to require you knowing which page to look in.",
    },
    {
      t: "p",
      text: "**The limitation:** Notion's block structure is verbose. When Claude creates a page, it can only create simple content — paragraphs, bullets, headings. It can't create toggle lists, callout blocks, or inline databases through the API. If your Notion style relies heavily on these, you'll be disappointed.",
    },
    {
      t: "h2",
      text: "Open-Meteo — surprisingly broadly useful",
    },
    {
      t: "p",
      text: "Weather data sounds niche until you start building planning workflows. Open-Meteo is free, needs no API key, and covers historical and forecast data back to 1940.",
    },
    {
      t: "p",
      text: "The use case that surprised me: event planning. \"Is there a risk of rain in Austin on June 14th?\" takes 3 seconds with this connected. More interestingly, Claude can cross-reference weather data with other context: \"I'm planning an outdoor meeting in London on these three possible dates — which has the best forecast?\"",
    },
    {
      t: "p",
      text: "For data teams, the historical data is genuinely useful: \"Pull daily max temperatures in Phoenix for the last 5 summers and tell me if this summer is anomalous so far.\"",
    },
    {
      t: "callout",
      kind: "tip",
      text: "This is the one MCP server I'd recommend having connected all the time, even if you don't think you need weather data. You'll discover use cases.",
    },
    {
      t: "h2",
      text: "The pattern across all of them",
    },
    {
      t: "p",
      text: "Looking at what makes each of these worth using, there's a consistent pattern: they all give Claude access to **ground truth that would otherwise require context pollution**.",
    },
    {
      t: "p",
      text: "Before MCP, getting Claude to help with anything live required copying data into the prompt — a snapshot that's instantly stale. The MCPs that work best are the ones where the live data matters. Customer subscription status changes. Issue priorities change. Weather obviously changes. Code search needs to see the current state of the repo.",
    },
    {
      t: "p",
      text: "The MCPs that don't hold up are the ones where the data is mostly static or where Claude's training data is already good enough. You don't need an MCP to ask Claude about Postgres syntax — it already knows it. You do need one to ask about your specific database schema.",
    },
    {
      t: "h2",
      text: "Setting them up",
    },
    {
      t: "p",
      text: "All of these are available in the [MCP Registry](https://apitomcpconverter.com/registry). You can connect them directly from there, or grab the Claude Desktop config snippet from each server's page. The whole setup for any of them is under 2 minutes — get the API key, paste the config, restart Claude Desktop.",
    },
    {
      t: "divider",
    },
    {
      t: "p",
      text: "The best MCPs aren't the most ambitious ones. They're the ones that make a specific 3-minute workflow take 10 seconds. Start with one API you use every day, connect it, and build the habit. The value compounds from there.",
    },
  ],
};

// ─── Exports ──────────────────────────────────────────────────────────────────

export const BLOG_POSTS: BlogPost[] = [post1, post2, post3];

export function getBlogPostBySlug(slug: string): BlogPost | undefined {
  return BLOG_POSTS.find((p) => p.slug === slug);
}
